import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SendIcon, CalendarClockIcon, BookmarkPlusIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import type { ScheduleState, SendMode } from '../../types/composer';
interface SendSchedulerProps {
  schedule: ScheduleState;
  onChange: (patch: Partial<ScheduleState>) => void;
  /** Label of the category the template would be filed under. */
  categoryLabel: string | null;
}
export function SendScheduler({
  schedule,
  onChange,
  categoryLabel
}: SendSchedulerProps) {
  const {
    t
  } = useLanguage();
  const options: {
    id: SendMode;
    icon: typeof SendIcon;
    title: string;
    description: string;
  }[] = [{
    id: 'now',
    icon: SendIcon,
    title: t('Envoyer maintenant', 'Send now'),
    description: t('Diffusion immédiate sur tous les canaux actifs.', 'Immediate delivery across every active channel.')
  }, {
    id: 'scheduled',
    icon: CalendarClockIcon,
    title: t("Programmer l'envoi", 'Schedule sending'),
    description: t('Choisissez la date et l’heure de diffusion.', 'Pick the delivery date and time.')
  }];
  const today = new Date().toISOString().slice(0, 10);
  return <section className="space-y-6" data-id="element-4425" data-mp="1fmf2vwdjc3sz">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-4426" data-mp="1fngrz4rdyqtp">
        {t("Moment de l'envoi", 'Sending time')}
      </h2>

      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-5" data-id="element-4427" data-mp="23em69fhohb1w">
        <div role="radiogroup" aria-label={t("Moment de l'envoi", 'Sending time')} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4428" data-mp="27dbfcvvoo6fq">
          {options.map(opt => {
          const Icon = opt.icon;
          const active = schedule.mode === opt.id;
          return <button key={opt.id} type="button" role="radio" aria-checked={active} onClick={() => onChange({
            mode: opt.id
          })} className={`text-left p-4 rounded-xl border transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/40 ring-1 ring-[#4F46E5]' : 'border-slate-200 hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4429" data-mp="2m9iydsvcv1tf">
                <span className={`inline-flex w-9 h-9 rounded-lg items-center justify-center mb-3 ${active ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-4430" data-mp="2hz8583d5icpb">
                  <Icon className="w-4 h-4" data-id="element-4431" data-mp="28a6rkju2w7ei" />
                </span>
                <span className="block text-sm font-semibold text-slate-900" data-id="element-4432" data-mp="23au3xwvduwav">
                  {opt.title}
                </span>
                <span className="block text-xs text-slate-500 mt-1 leading-snug" data-id="element-4433" data-mp="1y9xr2tt1zztb">
                  {opt.description}
                </span>
              </button>;
        })}
        </div>

        <AnimatePresence mode="wait" data-id="element-4434" data-mp="9qtj85makqxo">
          {schedule.mode === 'scheduled' && <motion.div key="scheduled" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-4435" data-mp="ds5d4nc77ybo">
              <div className="pt-1 flex flex-col sm:flex-row gap-4" data-id="element-4436" data-mp="3mab6j0xfktyx">
                <div className="flex-1" data-id="element-4437" data-mp="3r7jtdetixm6i">
                  <label htmlFor="send-date" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4438" data-mp="3r8ml1oyo7snh">
                    {t("Date d'envoi", 'Sending date')}
                  </label>
                  <input id="send-date" type="date" min={today} value={schedule.date} onChange={e => onChange({
                date: e.target.value
              })} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4439" data-mp="33is4oxf57lyh" />
                </div>
                <div className="w-full sm:w-44" data-id="element-4440" data-mp="1nsrc8n2l7004">
                  <label htmlFor="send-time" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4441" data-mp="1izy4rrfqhdkc">
                    {t('Heure', 'Time')}
                  </label>
                  <input id="send-time" type="time" value={schedule.time} onChange={e => onChange({
                time: e.target.value
              })} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4442" data-mp="1spho0y7cz9fq" />
                </div>
              </div>
            </motion.div>}

          {schedule.mode === 'template' && <motion.div key="template" initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.2
        }} className="overflow-hidden" data-id="element-4443" data-mp="2j11ug4nu8buz">
              <div className="pt-1 space-y-4" data-id="element-4444" data-mp="2e7gtq4uv13s3">
                <div data-id="element-4445" data-mp="209its4had9w4">
                  <label htmlFor="template-name" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4446" data-mp="2090ror7y01ga">
                    {t('Nom du modèle', 'Template name')}
                  </label>
                  <input id="template-name" type="text" value={schedule.templateName} onChange={e => onChange({
                templateName: e.target.value
              })} placeholder={t('Ex. : Bris d’aqueduc — secteur nord', 'E.g. Water main break — north district')} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4447" data-mp="1vapce0yvoyqo" />
                </div>
                <div data-id="element-4448" data-mp="oh9u497hxa2l">
                  <label htmlFor="template-description" className="block text-sm font-medium text-slate-700 mb-1" data-id="element-4449" data-mp="oi2pyf4x2f5y">
                    {t('Description', 'Description')}
                    <span className="text-slate-400 font-normal" data-id="element-4450" data-mp="1h7jev2tbvdxf">
                      {' '}
                      · {t('optionnel', 'optional')}
                    </span>
                  </label>
                  <input id="template-description" type="text" value={schedule.templateDescription} onChange={e => onChange({
                templateDescription: e.target.value
              })} placeholder={t('Quand utiliser ce modèle ?', 'When should this template be used?')} className="block w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5]" data-id="element-4451" data-mp="17djefe8zqwv1" />
                </div>
                <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5" data-id="element-4452" data-mp="2qdik9yw4c90i">
                  {t('Classé dans la catégorie : ', 'Filed under category: ')}
                  <span className="font-semibold text-slate-700" data-id="element-4453" data-mp="304dblho81yst">
                    {categoryLabel ?? t('à choisir plus haut', 'to be chosen above')}
                  </span>
                </p>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}